/*
 * ATHL8, Favorites (Wishlist)
 *
 * localStorage-backed wishlist with a custom-element button and a global
 * event bus so any number of heart buttons on a page stay in sync.
 *
 * Public API: window.ATHL8.favorites
 *   .list()                       → [{ id, handle, added_at }, ...]
 *   .has(handle)                  → boolean
 *   .add(handle, id)              → list
 *   .remove(handle)               → list
 *   .toggle(handle, id)           → list
 *   .clear()                      → []
 *   .count()                      → number
 *
 * Events fired on document:
 *   'athl8:favorites:changed'  detail = { count, ids, handles }
 *
 * Custom element <favorite-button data-product-handle data-product-id>
 *   - Renders aria-pressed state based on current list membership.
 *   - On click, toggles + dispatches the event.
 *   - Listens to the event so two hearts for the same product on one
 *     page (e.g. PLP card + recently-viewed strip) stay in lockstep.
 *
 * Header indicator: any element with [data-favorites-count] gets its
 * text + hidden attribute updated on every change.
 */

(() => {
  'use strict';

  const STORAGE_KEY = 'athl8_favorites';
  const EVENT_NAME = 'athl8:favorites:changed';

  function read() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  }

  function write(list) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (_) {
      // Quota or private-browsing: silently ignore.
    }
  }

  function broadcast(list) {
    const detail = {
      count: list.length,
      ids: list.map((e) => e.id),
      handles: list.map((e) => e.handle),
    };
    document.dispatchEvent(new CustomEvent(EVENT_NAME, { detail }));
  }

  const api = {
    list() {
      return read();
    },
    has(handle) {
      if (!handle) return false;
      return read().some((e) => e.handle === handle);
    },
    add(handle, id) {
      if (!handle) return read();
      const list = read();
      if (list.some((e) => e.handle === handle)) return list;
      const next = list.concat([{ id: id || null, handle: handle, added_at: Date.now() }]);
      write(next);
      broadcast(next);
      return next;
    },
    remove(handle) {
      if (!handle) return read();
      const next = read().filter((e) => e.handle !== handle);
      write(next);
      broadcast(next);
      return next;
    },
    toggle(handle, id) {
      return this.has(handle) ? this.remove(handle) : this.add(handle, id);
    },
    clear() {
      write([]);
      broadcast([]);
      return [];
    },
    count() {
      return read().length;
    },
  };

  window.ATHL8 = window.ATHL8 || {};
  window.ATHL8.favorites = api;

  // -----------------------------------------------------------------
  // <favorite-button> custom element
  // -----------------------------------------------------------------
  class FavoriteButton extends HTMLElement {
    connectedCallback() {
      this.handle = this.getAttribute('data-product-handle') || '';
      this.productId = this.getAttribute('data-product-id') || '';
      this.btn = this.querySelector('button');
      if (!this.btn) return;

      this.localeAdd = this.btn.getAttribute('data-label-add') || this.btn.getAttribute('aria-label') || '';
      this.localeRemove = this.btn.getAttribute('data-label-remove') || this.localeAdd;

      this.refreshState();
      this.btn.addEventListener('click', this.onClick);
      document.addEventListener(EVENT_NAME, this.onChanged);
    }

    disconnectedCallback() {
      if (this.btn) this.btn.removeEventListener('click', this.onClick);
      document.removeEventListener(EVENT_NAME, this.onChanged);
    }

    onClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!this.handle) return;
      api.toggle(this.handle, this.productId);
      // The 'changed' event handler picks up the new state. But we also
      // play a quick scale animation on this exact button for tactile
      // feedback before the broadcast lands.
      this.btn.classList.remove('favorite-btn__inner--pulse');
      // Force reflow so the class re-adds cleanly even on rapid taps.
      void this.btn.offsetWidth;
      this.btn.classList.add('favorite-btn__inner--pulse');
    };

    onChanged = () => {
      this.refreshState();
    };

    refreshState() {
      const active = api.has(this.handle);
      this.btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      this.btn.setAttribute('aria-label', active ? this.localeRemove : this.localeAdd);
      this.classList.toggle('is-active', active);
    }
  }

  if (!customElements.get('favorite-button')) {
    customElements.define('favorite-button', FavoriteButton);
  }

  // -----------------------------------------------------------------
  // Header / generic count indicator
  // -----------------------------------------------------------------
  function syncCountElements(count) {
    document.querySelectorAll('[data-favorites-count]').forEach((el) => {
      el.textContent = String(count);
      if (count > 0) el.removeAttribute('hidden');
      else el.setAttribute('hidden', '');
    });
  }

  document.addEventListener(EVENT_NAME, (e) => {
    syncCountElements(e.detail.count);
  });

  // -----------------------------------------------------------------
  // Header trigger: open the favorites drawer.
  // Uses capture phase so this fires BEFORE Symmetry's main.js delegated
  // .cart-link click handler that opens the cart drawer.
  // -----------------------------------------------------------------
  function onTriggerClick(e) {
    const trigger = e.target.closest('[data-favorites-trigger]');
    if (!trigger) return;
    const drawer = document.getElementById('favorites-drawer');
    if (!drawer) return; // No drawer yet → fall through to the link's href.
    e.preventDefault();
    e.stopImmediatePropagation();
    if (typeof drawer.open === 'function') {
      drawer.open(trigger);
    } else {
      // Drawer hasn't upgraded yet: wait a tick.
      setTimeout(() => drawer.open && drawer.open(trigger), 50);
    }
  }
  document.addEventListener('click', onTriggerClick, true);

  // Run once on initial load to hydrate header badge from whatever
  // localStorage already holds.
  function init() {
    syncCountElements(api.count());
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
